import React, { useState } from "react";
import Sidebar from "../../components/Sidebar";
import "./record_mileage_company.css";
import {
  FaSave,
  FaSpinner,
  FaMapMarkerAlt,
  FaRoute,
  FaArrowRight,
  FaCloudUploadAlt,
  FaTimes,
  FaTachometerAlt,
  FaCheckCircle,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  recordMileageApi,
  takeImageUploadExpenseApi,
} from "../../services/apiCall";

export default function RecordMileageCompanyPage() {
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [miles, setMiles] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);

  const userId = useSelector((state) => state?.user?.userId);
  const businessId = useSelector((state) => state?.user?.businessId);
  const businessName =
    useSelector((state) => state?.user?.businessName) || "Your Business";

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
    setImage(file);
    setIsExtracting(true);
    setExtracted(false);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("userId", userId);
    formData.append("businessId", businessId);
    formData.append("type", "mileage");

    try {
      const response = await takeImageUploadExpenseApi(formData);
      const data = response?.data?.data;

      if (data) {
        if (data.from) setFrom(data.from);
        if (data.to) setTo(data.to);
        if (data.miles) setMiles(data.miles.toString());
        if (data.reason) setReason(data.reason);
        if (data.description) setDescription(data.description);
        setExtracted(true);
        toast.success("Mileage doc scanned — fields auto-filled!");
      }
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error);
      toast.error("Failed to extract data from document.");
    } finally {
      setIsExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!from || !to || !reason || !description || !miles) {
      toast.error("Please fill all required fields.");
      return;
    }

    const milesValue = parseFloat(miles);
    if (isNaN(milesValue) || milesValue <= 0) {
      toast.error("Miles must be a positive number.");
      return;
    }

    const payload = {
      businessId,
      reason,
      description,
      from,
      to,
      miles: milesValue,
      createdBy: userId,
    };

    try {
      setIsLoading(true);
      const response = await recordMileageApi(payload);

      if (response?.data?.mileage != null) {
        toast.success("Mileage record created successfully!");
        setReason(""); setDescription(""); setMiles("");
        setFrom(""); setTo("");
        setImage(null); setImagePreview(null); setExtracted(false);
      } else {
        toast.error("Unexpected server response. Please try again.");
      }
    } catch (error) {
      console.error("Error creating record:", error);
      toast.error(
        error?.response?.data?.message || "Something went wrong while saving."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h2 className="dashboard-title">Record Mileage</h2>
            <p className="dashboard-sub">Record your business travel mileage</p>
          </div>
          <div className="dashboard-info">
            <h3 className="dashboard-company">{businessName}</h3>
            <p className="dashboard-date">{today}</p>
          </div>
        </div>

        {/* Card */}
        <div className="mileage-card">
          <div className="card-header-strip">
            <FaRoute />
            <h3>Journey Details</h3>
          </div>

          <div className="mileage-form-body">
            {/* Reason */}
            <div className="form-group">
              <label>Reason for Travel</label>
              <input
                type="text"
                placeholder="e.g. Client Meeting, Site Visit"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            {/* From → To */}
            <div className="route-row">
              <div className="form-group">
                <label>From <span className="req">*</span></label>
                <div className="input-icon-wrap">
                  <FaMapMarkerAlt className="fi-icon" style={{ color: "#0ea5e9" }} />
                  <input
                    type="text"
                    placeholder="Start location"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    style={{ paddingLeft: "2.6rem" }}
                  />
                </div>
              </div>

              <div className="route-arrow">
                <FaArrowRight />
              </div>

              <div className="form-group">
                <label>To <span className="req">*</span></label>
                <div className="input-icon-wrap">
                  <FaMapMarkerAlt className="fi-icon" style={{ color: "#ef4444" }} />
                  <input
                    type="text"
                    placeholder="Destination"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    style={{ paddingLeft: "2.6rem" }}
                  />
                </div>
              </div>
            </div>

            {/* Two-col: Miles + empty */}
            <div className="two-col">
              <div className="form-group">
                <label>Miles Traveled <span className="req">*</span></label>
                <div className="miles-suffix-wrap input-icon-wrap">
                  <FaTachometerAlt className="fi-icon" />
                  <input
                    type="number"
                    placeholder="0"
                    value={miles}
                    onChange={(e) => setMiles(e.target.value)}
                    style={{ paddingLeft: "2.6rem", paddingRight: "3.2rem" }}
                  />
                  <span className="miles-unit">mi</span>
                </div>
              </div>
              {miles && (
                <div className="form-group">
                  <label>Estimated Allowance</label>
                  <input
                    type="text"
                    readOnly
                    value={`£${(parseFloat(miles || 0) * 0.45).toFixed(2)}`}
                    style={{ background: "#f4f5f9", color: "#059669", fontWeight: 600 }}
                  />
                </div>
              )}
            </div>

            {/* Description */}
            <div className="form-group">
              <label>Trip Description</label>
              <textarea
                placeholder="Enter any additional trip details…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Upload */}
            <div className="form-group">
              <label>Attach Document (optional)</label>
              <div className="upload-zone">
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleImageChange}
                />
                <div className="upload-icon-wrap">
                  {isExtracting ? (
                    <FaSpinner className="spin" style={{ color: "#fff", fontSize: "1.2rem" }} />
                  ) : (
                    <FaCloudUploadAlt />
                  )}
                </div>
                <h4>{image ? image.name : "Upload mileage log or receipt"}</h4>
                <p>Drag & drop or click to browse</p>
                <span className="upload-hint">Supports: JPG, PNG, PDF</span>
                {isExtracting && (
                  <div className="extracting-badge">
                    <FaSpinner className="spin" /> Extracting data…
                  </div>
                )}
                {extracted && !isExtracting && (
                  <div className="extracting-badge" style={{ background: "#dcfce7", color: "#16a34a" }}>
                    <FaCheckCircle /> Fields auto-filled!
                  </div>
                )}
              </div>

              {imagePreview && (
                <div className="receipt-preview">
                  <img src={imagePreview} alt="Document preview" />
                  <button
                    className="remove-img"
                    onClick={() => { setImage(null); setImagePreview(null); setExtracted(false); }}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="footer-save">
            <button className="btn-save" onClick={handleSave} disabled={isLoading}>
              {isLoading ? (
                <><FaSpinner className="spin" /> Saving…</>
              ) : (
                <><FaSave /> Save Mileage Record</>
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
